import { z } from 'zod';

import { CursorPaginationMetaSchema, PaginationSchema } from '../../pagination.js';

export const CatalogSearchKindSchema = z.enum(['GENERIC', 'GENERIC_SERVICE', 'PRODUCT', 'SERVICE']);
export type CatalogSearchKind = z.infer<typeof CatalogSearchKindSchema>;

export const CatalogSearchScopeSchema = z.enum(['GENERIC', 'PRODUCT', 'SERVICE', 'LINE']);
export type CatalogSearchScope = z.infer<typeof CatalogSearchScopeSchema>;

function preprocessKinds(value: unknown): unknown {
  if (value === undefined || value === '') {
    return undefined;
  }
  if (Array.isArray(value)) {
    return value;
  }
  if (typeof value === 'string') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
  }
  return value;
}

export const CatalogSearchQuerySchema = PaginationSchema.extend({
  scope: CatalogSearchScopeSchema,
  q: z.preprocess(
    (val) => (typeof val === 'string' && val.trim() === '' ? undefined : val),
    z.string().trim().min(2).max(100).optional(),
  ),
  vehicleModelId: z.uuid().optional(),
  manufactureYear: z.coerce.number().int().min(1900).max(2100).optional(),
  vehicleEngineId: z.uuid().optional(),
  vehicleGenerationId: z.uuid().optional(),
  vehicleTrimId: z.uuid().optional(),
  placeId: z.uuid().optional(),
  kinds: z.preprocess(preprocessKinds, z.array(CatalogSearchKindSchema).optional()),
  categoryId: z.uuid().optional(),
  subcategoryId: z.uuid().optional(),
  genericProductId: z.uuid().optional(),
});

export const CatalogApplicabilitySchema = z.enum(['APPLICABLE', 'UNDEFINED']);
export type CatalogApplicability = z.infer<typeof CatalogApplicabilitySchema>;

export const CatalogSearchOriginSchema = z.enum(['TENANT', 'GLOBAL']);
export type CatalogSearchOrigin = z.infer<typeof CatalogSearchOriginSchema>;

const CatalogSearchLineEnrichmentFields = {
  brandName: z.string().nullable().optional(),
  unitCostCents: z.string().nullable().optional(),
  unitPriceCents: z.string().nullable().optional(),
  stockQuantity: z.string().nullable().optional(),
  usefulLifeMeters: z.number().int().nullable().optional(),
  usefulLifeMonths: z.number().int().nullable().optional(),
};

/** BR-PRD-104 — GLOBAL hits are LINE-only unverified tenant copies; default TENANT. */
const CatalogSearchOriginFields = {
  origin: CatalogSearchOriginSchema.default('TENANT'),
  catalogProductId: z.uuid().nullable().optional(),
};

export const CatalogSearchProductHitSchema = z.object({
  kind: z.literal('PRODUCT'),
  id: z.uuid(),
  name: z.string(),
  code: z.string().nullable(),
  sku: z.string().nullable(),
  placeProductId: z.uuid().nullable(),
  applicability: CatalogApplicabilitySchema,
  ...CatalogSearchOriginFields,
  ...CatalogSearchLineEnrichmentFields,
});

export const CatalogSearchServiceHitSchema = z.object({
  kind: z.literal('SERVICE'),
  id: z.uuid(),
  name: z.string(),
  code: z.string().nullable(),
  sku: z.null(),
  placeProductId: z.null(),
  applicability: CatalogApplicabilitySchema,
  ...CatalogSearchOriginFields,
  ...CatalogSearchLineEnrichmentFields,
});

export const CatalogSearchGenericHitSchema = z.object({
  kind: z.literal('GENERIC'),
  id: z.uuid(),
  name: z.string(),
  code: z.null(),
  sku: z.null(),
  placeProductId: z.null(),
  applicability: CatalogApplicabilitySchema,
  ...CatalogSearchOriginFields,
  ...CatalogSearchLineEnrichmentFields,
});

export const CatalogSearchGenericServiceHitSchema = z.object({
  kind: z.literal('GENERIC_SERVICE'),
  id: z.uuid(),
  name: z.string(),
  code: z.null(),
  sku: z.null(),
  placeProductId: z.null(),
  applicability: CatalogApplicabilitySchema,
  ...CatalogSearchOriginFields,
  ...CatalogSearchLineEnrichmentFields,
});

export const CatalogSearchHitSchema = z.discriminatedUnion('kind', [
  CatalogSearchProductHitSchema,
  CatalogSearchServiceHitSchema,
  CatalogSearchGenericHitSchema,
  CatalogSearchGenericServiceHitSchema,
]);

export const CatalogSearchResponseSchema = z.object({
  items: z.array(CatalogSearchHitSchema),
  pagination: CursorPaginationMetaSchema,
});

export type CatalogSearchQuery = z.infer<typeof CatalogSearchQuerySchema>;
export type CatalogSearchHit = z.infer<typeof CatalogSearchHitSchema>;
export type CatalogSearchResponse = z.infer<typeof CatalogSearchResponseSchema>;
